import {
  Box,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import InfoBadge, { OriginEnum } from "../../common/info-badge/InfoBadge";
import MessageCard from "./MessageCard";
import { useLangContext } from "../../../context/langContext";
import { TicketDetailsRequest } from "../../../services/Endpoints/tickets/TicketDetailsEndpoint";
import {
  useFormDataAxiosInstance,
  useProtectedAxiosInstance,
} from "../../../hooks/axiosInstance";
import { useEffect, useRef, useState, useCallback } from "react";
import { TicketDetailsSchema } from "../../../services/Schemas/Ticket";
import { dateFormatter } from "../../../utils/date-formatter";
import { TicketNoteCreateRequest } from "../../../services/Endpoints/tickets/TicketNoteCreateEndpoint";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import ReloadButton from "../../common/reload-button/ReloadButton";
import constants from "../../../constants/constants";
import { useNavigate } from "react-router-dom";
import { IUserLink } from "../common/UserLink";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { CloseCircle } from "iconsax-react";
import { useDropzone } from "react-dropzone";

interface ITicketDetailsBoardProps {
  ticketNumber: string;
  setCreator: (payload: IUserLink["user"]) => void;
  setStatus: (payload: string) => void;
}

const infos = {
  en: {
    title: "عنوان تیکت",
    ticketNumber: "کد پیگیری",
    created_at: "تاریخ ایجاد",
  },
  fa: {
    title: "عنوان تیکت",
    ticketNumber: "کد پیگیری",
    created_at: "تاریخ ایجاد",
  },
};

const fileRejectError = {
  fa: "فایل با حجم یا نوع نادرست.",
  en: "Invalid file type or size.",
};

export default function TicketDetailsBoardComponent({
  ticketNumber,
  setCreator,
  setStatus,
}: Readonly<ITicketDetailsBoardProps>) {

  const { dir, lang } = useLangContext();
  const [data, setData] = useState<TicketDetailsSchema | undefined>(undefined);
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const protectedFormDataInstance = useFormDataAxiosInstance();
  const { setNotification } = useNotificationContext();
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [fileData, setFileData] = useState<{
    file: File;
    preview: string;
  } | null>(null);
  const [reload, setReload] = useState(false);
  const navigate = useNavigate();
  const { can } = useCanAccess();

  const messageEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [data]);

  const getData = async () => {
    try {
      setWaiting(true);
      const ticketDetailsRequest = new TicketDetailsRequest(protectedInstance);
      const { data } = await ticketDetailsRequest.send({ ticketNumber });
      setData(data.ticket);
      setCreator(data.ticket.creator);
      setStatus(data.ticket.status);
      setWaiting(false);
    } catch (error: any) {
      errorResolver({ error, setNotification });
      navigate("/404");
      console.error(error.message);
    }
  };

  useEffect(() => {
    getData();
  }, [reload]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!description) return;

    setWaiting(true);
    try {
      const ticketNoteCreateEndpoint = new TicketNoteCreateRequest(
        protectedFormDataInstance
      );
      const formData = new FormData();
      formData.append("description", description);
      formData.append("is_private", String(isPrivate ? 1 : 0));
      if (fileData?.file) {
        formData.append("image", fileData.file);
      }
     
      await ticketNoteCreateEndpoint.sendFormData({ ticketNumber }, formData);

      setDescription("");
      setIsPrivate(false);
      setFileData(null);
      await getData();
    } catch (error: any) {
      errorResolver({ error, setNotification });
      setDescription("");
      setIsPrivate(false);
      setFileData(null);
      setWaiting(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: any) => {
      if (fileRejections.length > 0) {
        setNotification({ type: "error", message: fileRejectError[lang] });
        return;
      }

      const uploadedFile = acceptedFiles[0];
      const reader = new FileReader();

      reader.onload = () => {
        setFileData({ file: uploadedFile, preview: reader.result as string });
      };

      reader.readAsDataURL(uploadedFile);
    },
    [lang]
  );

  const { getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/gif": [],
      "image/webp": [],
      "image/svg+xml": [],
    },
    maxSize: 5000000,
  });

  return data ? (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        backgroundColor: (theme) => theme.palette.gray[50],
        borderRadius: "6px",
        minHeight: "70vh",
        position: "relative",
      }}
    >
      {waiting && <LinearProgress />}

      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center", xs: "start" }}
        gap="12px"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: "100%",
          px: "18px",
          py: "16px",
        }}
      >
        <Stack direction="column" alignItems="start" gap="4px">
          <Typography
            variant="h6Regular"
            color={(theme) => theme.palette.dominant[400]}
            sx={{ display: "flex", gap: "5px" }}
          >
            {infos[lang].title}:
            <Typography
              variant="h6MediumBold"
              color={(theme) => theme.palette.dominant[400]}
            >
              {data.caption}
            </Typography>
          </Typography>
          <InfoBadge
            origin={OriginEnum.TICKET}
            status={data.status as "pending"}
            mx="0"
          />
        </Stack>
        <Stack direction="column" alignItems="end" gap="4px">
          <Stack direction="row" gap="6px" alignItems="start">
            <ReloadButton
              reloadAction={() => setReload(!reload)}
              reloadCounter={constants.ticketReloadReqTime}
            />
            <Stack
              direction="row"
              justifyContent="flex-end"
              color={(theme) => theme.palette.gray.bg}
              bgcolor={(theme) => theme.palette.dominant.main}
              gap={{ md: "12px", xs: "4px" }}
              sx={{
                px: "16px",
                py: "2px",
                borderRadius: "6px",
              }}
            >
              <Typography
                variant="h6Regular"
                color={(theme) => theme.palette.dominant[100]}
              >
                {infos[lang].ticketNumber}:
              </Typography>
              <Typography
                variant="h6Regular"
                color={(theme) => theme.palette.dominant[100]}
              >
                {data.ticket_number}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Typography
              variant="labelRegular"
              color={(theme) => theme.palette.dominant[400]}
            >
              {infos[lang].created_at}:
            </Typography>
            <Typography
              variant="labelMediumBold"
              color={(theme) => theme.palette.dark[40]}
            >
              {dateFormatter({ lang, date: data.created_at })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Messages */}
      <Stack
        my="5px"
        mx="18px"
        gap="28px"
        height={"40vh"}
        sx={{ overflowY: "auto" }}
      >
        {data.activities.map(
          ({
            created_at,
            description,
            file,
            user,
            creator_role,
            is_private,
          }) => (
            <MessageCard
              key={description}
              messageText={description}
              name={user.name}
              createdAt={created_at}
              isPrivate={!!is_private}
              isSupportUser={creator_role !== "user"}
              messageImage={file}
            />
          )
        )}
        <div ref={messageEndRef} />
      </Stack>

      {/* Input Section */}
      <Stack
        component="form"
        onSubmit={handleSubmit}
        mx="16px"
        pt="20px"
        width="96%"
        sx={{ backgroundColor: "white" }}
      >
        <Stack
          dir="ltr"
          direction="row"
          justifyContent="start"
          alignItems="end"
        >
          <Box width="100%" position="relative">
            <input disabled  type="file" hidden {...getInputProps()} id="fileAttached" />

            <Box
              sx={{
                borderColor: (theme) => theme.palette.dominant.main,
                borderStyle: "solid",
                borderWidth: ".8px",
                borderRadius: "16px ",
                width: "calc(100% - 5px)",
                height: "fit-content",
              }}
            >
              {fileData && (
                <Stack
                  sx={{
                    width: "105px",
                    height: "78px",
                    overflow: "hidden",
                    marginTop: "5px",
                    marginLeft: "auto",
                    marginRight: "35px",
                    position: "relative",
                  }}
                >
                  <IconButton
                    onClick={() => setFileData(null)}
                    sx={{
                      position: "absolute",
                      top: "5px",
                      zIndex: 10,
                      color: (theme) => theme.palette.others.deadline,
                    }}
                  >
                    <CloseCircle size="18" />
                  </IconButton>
                  <img
                    src={fileData.preview}
                    alt="uploaded"
                    width="105px"
                    height="78px"
                    style={{
                      border: "1px solid black",
                      borderRadius: "5px",
                    }}
                  />
                </Stack>
              )}

              <Box
                dir={dir}
                component="textarea"
                name="ticket_message"
                sx={{
                  fontFamily: "IRANYekanX",
                  width: "calc(100% - 5px)",
                  py: "10px",
                  mb: "4px",
                  px: "34px",
                  borderRadius: "16px",
                  fontSize: "16px",
                  outline: "none",
                  border: "none",
                  height: "40px",
                  resize: "none",
                  scrollbarWidth: "none",
                  "::placeholder": {
                    fontSize: "12.64px",
                    color: (theme) => theme.palette.gray[400],
                  },
                }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="چیزی تایپ کنید ...."
              />
            </Box>

            {waiting && (
              <Box position="absolute" left="16px" top="10px" zIndex="100">
                <CircularProgress size="26px" />
              </Box>
            )}

            {!fileData && (
              <Box
                sx={{ cursor: "pointer" }}
                component="label"
                htmlFor="fileAttached"
                position="absolute"
                right="16px"
                top="8px"
              >
                <img src="/images/attachedIcon.svg" />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              cursor: waiting ? "not-allowed" : "pointer",
              border: "none",
              outline: "none",
              borderRadius: "50%",
            }}
            component={"button"}
            disabled={waiting || !can(AccessEnums.AdminTicketsAnswer)}
          >
            <img src="/images/sendIcon.svg" />
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="start"
          gap="1px"
        >
          <Checkbox
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
          />
          <Typography variant="warningRegular">
            چت خصوصی (محتوا این پیام به کاربر نمایش داده نمیشود)
          </Typography>
        </Stack>
      </Stack>
    </Box>
  ) : (
    <LinearProgress />
  );
}
